import { defineMessages } from "react-intl";

/**
 * Screen copy. `{taxType}` stands in for VAT / GST / QST throughout, so one string
 * serves every region — the same approach `TaxDetails.messages.ts` takes.
 */
const TAX_DETAILS_FORM_MESSAGES = defineMessages({
  addHeading: {
    id: "tax_details_form.add_heading",
    defaultMessage: "Add {taxType} details",
  },
  updateHeading: {
    id: "tax_details_form.update_heading",
    defaultMessage: "Update {taxType} details",
  },
  explainer: {
    id: "tax_details_form.explainer",
    defaultMessage:
      "Adding your {taxType} registration means your {taxType} ID and rate will appear on your invoices and booking receipts. It’s the host’s responsibility for keeping {taxType} details accurate and up to date. Incorrect details may affect your tax filings.",
  },
  supportLink: {
    id: "tax_details_form.support_link",
    defaultMessage: "How does {taxType} work?",
  },
  businessNameLabel: {
    id: "tax_details_form.business_name_label",
    defaultMessage: "Business Name",
  },
  businessNameSubcopy: {
    id: "tax_details_form.business_name_subcopy",
    defaultMessage:
      "Business name as it appears on your {taxType} registration.",
  },
  taxIdLabel: {
    id: "tax_details_form.tax_id_label",
    defaultMessage: "{taxType} ID",
  },
  taxIdSubcopy: {
    id: "tax_details_form.tax_id_subcopy",
    defaultMessage: "Enter your {taxType} number.",
  },
  taxIdTooltipTrigger: {
    id: "tax_details_form.tax_id_tooltip_trigger",
    defaultMessage: "Where do I find my {taxType} ID?",
  },
  taxIdTooltipBody: {
    id: "tax_details_form.tax_id_tooltip_body",
    defaultMessage:
      "Check your business’s tax registration documents, or your national tax authority’s website.",
  },
  taxRateLabel: {
    id: "tax_details_form.tax_rate_label",
    defaultMessage: "{taxType} %",
  },
  taxRateSubcopy: {
    id: "tax_details_form.tax_rate_subcopy",
    defaultMessage:
      "Enter a number less than {maxRate}, without the % symbol. This won’t change your prices. It’s a record of what you’ve already included in your hourly rate and cleaning fees.",
  },
  addressHeading: {
    id: "tax_details_form.address_heading",
    defaultMessage: "{taxType} registered address",
  },
  addressTooltipTrigger: {
    id: "tax_details_form.address_tooltip_trigger",
    defaultMessage: "Your {taxType} address",
  },
  addressTooltipBody: {
    id: "tax_details_form.address_tooltip_body",
    defaultMessage:
      "This may be different from your account address if you’re {taxType} registered under a separate business address.",
  },
  confirmationLabel: {
    id: "tax_details_form.confirmation_label",
    defaultMessage: "I confirm this information is accurate.",
  },
  /**
   * Shown when the sub-region form is pointed at a region that charges no
   * sub-region tax — Alberta, or anywhere in a country with no regional taxes at
   * all. There is nothing to register, so there is nothing to ask for.
   */
  noRegionalRegistration: {
    id: "tax_details_form.no_regional_registration",
    defaultMessage:
      "There is no separate regional tax registration for {region}.",
  },
  goToFederalForm: {
    id: "tax_details_form.go_to_federal_form",
    defaultMessage: "Add {taxType} details instead",
  },
  /**
   * The country is chosen but the region is not, so which tax applies is still
   * unanswered. Distinct from `noRegionalRegistration`, which reports a known
   * absence — here nothing is missing, the guest simply has more to fill in.
   */
  selectRegionForTaxDetails: {
    id: "tax_details_form.select_region_for_tax_details",
    defaultMessage:
      "Select your {regionLabel} to see which {taxType} details are required.",
  },
  /** Used where `@shopify/address` gives the country no word of its own. */
  defaultRegionLabel: {
    id: "tax_details_form.default_region_label",
    defaultMessage: "region",
  },
  /**
   * Names the place in `noRegionalRegistration` when no country is selected, which
   * is where a guest with neither an account country nor a locale that names one
   * starts. Without it the sentence renders as "…registration for ."
   */
  defaultRegionName: {
    id: "tax_details_form.default_region_name",
    defaultMessage: "your region",
  },
  addSubmit: {
    id: "tax_details_form.add_submit",
    defaultMessage: "Add tax details",
  },
  updateSubmit: {
    id: "tax_details_form.update_submit",
    defaultMessage: "Update tax details",
  },
  cancel: {
    id: "tax_details_form.cancel",
    defaultMessage: "Cancel",
  },
});

/**
 * Validation copy. Anything naming a tax type takes it as `{taxType}` rather than
 * baking "VAT" in, so one string serves every region.
 */
const TAX_DETAILS_FORM_ERROR_MESSAGES = defineMessages({
  businessNameRequired: {
    id: "tax_details_form.errors.business_name_required",
    defaultMessage: "Enter a business name.",
  },
  taxIdInvalid: {
    id: "tax_details_form.errors.tax_id_invalid",
    defaultMessage:
      "This doesn’t match the {taxType} number format for {country}. Please check and try again.",
  },
  taxRateInvalid: {
    id: "tax_details_form.errors.tax_rate_invalid",
    defaultMessage:
      "This doesn’t look like a valid {taxType} rate. Enter a number less than {maxRate}, without the % symbol.",
  },
  confirmationRequired: {
    id: "tax_details_form.errors.confirmation_required",
    defaultMessage: "Please confirm this information is accurate.",
  },
  /** Only reached when the API rejects without telling us why. */
  submitFailed: {
    id: "tax_details_form.errors.submit_failed",
    defaultMessage: "Something went wrong. Please try again.",
  },
});

export { TAX_DETAILS_FORM_ERROR_MESSAGES, TAX_DETAILS_FORM_MESSAGES };
